/**
 * Web app onboarding Zustand store
 *
 * Holds invitation id, invite preview, signed-up patient snapshot, verified names from
 * phone auth, and transient UI state (toasts, confirmation modal) for the flow.
 * Persisted to localStorage (`webAppOnboarding-default-store-v2`): `signUpId`,
 * `verifiedPatientNamesFromAuth`, and invite preview when last fetch was `ready`.
 * Transient invite/patient statuses and the Firestore snapshot are in-memory only.
 *
 * Route guards read this store; Firebase Auth remains the gate for protected routes.
 *
 * References:
 * - `api/coursePaymentCallables.ts`
 * - `api/webAppOnboardingAuthentication.ts`
 * - `hooks/useOnboardingSignUpGate.ts`
 */
import { SignedUpPatient } from "@physiofit-digital/physiofit_types";
import { create, StateCreator } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export type EventIndicatorType =
  | { type: EventIndicatorStateType; message: string; index: number }
  | undefined;
export type ConfirmationModalData =
  | {
      title: string;
      text: string;
      confirmText?: string;
      cancelText?: string;
      resolve: ((value: boolean) => void) | null;
    }
  | undefined;
export type EventIndicatorStateType = "error" | "success" | "info" | "warning";
/** Names returned by `webAppOnboardingAuthentication` after a successful phone check; complements the Firestore snapshot. */
export type VerifiedPatientNamesFromAuth = {
  firstName: string;
  lastName: string;
};

/** Preview fields from callable `getSignedUpPatientInviteContext` (cached for the flow). */
export type SignedUpPatientInviteContext = {
  practiceName: string;
  therapistFirstName: string;
  therapistLastName: string;
  therapistImageUrl?: string;
  courseName: string;
  courseId?: string;
  /** Practice logo (Cloudflare Images URL). Present only when the practice has the branding package. */
  practiceLogoUrl?: string;
  /** Practice charges private patients a fee for app use (PF-5963). Absent from older function versions. */
  practiceChargesPrivateFee?: boolean;
};

export type InviteContextStatus = 'idle' | 'loading' | 'ready' | 'not_found' | 'error';

export type SignedUpPatientStatus = 'idle' | 'loading' | 'ready' | 'not_found' | 'error';

type OnboardingState = {
  signUpId: string | undefined;
  setSignUpId: (id: string) => void;
  /** Raw `u` from invite URL until passed to `getSignedUpPatientInviteContext` (in-memory only). */
  pendingLinkUtmCode: string | undefined;
  setPendingLinkUtmCode: (code: string | undefined) => void;
  clearPendingLinkUtmCode: () => void;
  /** Cached header preview for {@link signUpId}; cleared when the id changes. */
  inviteContext: SignedUpPatientInviteContext | undefined;
  inviteContextStatus: InviteContextStatus;
  /** When set, {@link inviteContextStatus} / {@link inviteContext} reflect the last resolved fetch for this id. */
  inviteContextResolvedForSignUpId: string | undefined;
  beginInviteContextLoad: () => void;
  setInviteContextReady: (signUpId: string, data: SignedUpPatientInviteContext) => void;
  setInviteContextNotFound: (signUpId: string) => void;
  setInviteContextFailed: (signUpId: string) => void;
  /** Clears invite preview fetch state so `useInviteContext` can refetch (e.g. after error or SMS re-open). */
  resetInviteContextCache: () => void;
  /** Full `signedUpPatients/{signUpId}` snapshot for prefill and display (course, therapist, names, etc.). */
  signedUpPatient: SignedUpPatient | undefined;
  /** Firestore listener resolution for {@link signUpId}. */
  signedUpPatientStatus: SignedUpPatientStatus;
  setSignedUpPatientStatus: (status: SignedUpPatientStatus) => void;
  /** Bumped by {@link retrySignedUpPatientLoad}; `useSignedUpPatientConnect` re-subscribes on every change. */
  signedUpPatientListenerAttempt: number;
  retrySignedUpPatientLoad: () => void;
  /** Callable `patientFirstName` / `patientLastName` after successful phone auth. */
  verifiedPatientNamesFromAuth: VerifiedPatientNamesFromAuth | undefined;
  setVerifiedPatientNamesFromAuth: (
    names: VerifiedPatientNamesFromAuth | undefined,
  ) => void;
  eventIndicatorData?: EventIndicatorType;
  confirmationModalData?: ConfirmationModalData;
  setSignedUpPatient: (signedUpPatient: SignedUpPatient | undefined) => void;
  setConfirmationModalData: (
    confirmationModalData?: ConfirmationModalData,
  ) => void;
  setEventIndicatorData: (
    type: EventIndicatorStateType,
    message: string,
  ) => void;
  resetStore: () => void;
};

const initialState = {
  signUpId: undefined,
  pendingLinkUtmCode: undefined,
  inviteContext: undefined,
  inviteContextStatus: 'idle' as InviteContextStatus,
  inviteContextResolvedForSignUpId: undefined,
  signedUpPatient: undefined,
  signedUpPatientStatus: 'idle' as SignedUpPatientStatus,
  signedUpPatientListenerAttempt: 0,
  verifiedPatientNamesFromAuth: undefined,
  eventIndicatorData: undefined,
  confirmationModalData: undefined,
};

/**
 * Zustand slice factory for onboarding flow state and mutations.
 *
 * @return {Object} Initial state plus setters used by `useOnboardingStore`.
 */
export const createOnboardingSlice: StateCreator<
  OnboardingState,
  [["zustand/immer", never]],
  [],
  OnboardingState
> = (set) => ({
  ...initialState,
  setSignUpId: (id) =>
    set((state) => {
      if (state.signUpId !== id) {
        state.inviteContext = undefined;
        state.inviteContextStatus = 'idle';
        state.inviteContextResolvedForSignUpId = undefined;
        state.signedUpPatient = undefined;
        state.signedUpPatientStatus = 'idle';
        state.signedUpPatientListenerAttempt = 0;
        state.pendingLinkUtmCode = undefined;
      }
      state.signUpId = id;
    }),
  setPendingLinkUtmCode: (code) =>
    set((state) => {
      state.pendingLinkUtmCode = code?.trim() || undefined;
    }),
  clearPendingLinkUtmCode: () =>
    set((state) => {
      state.pendingLinkUtmCode = undefined;
    }),
  beginInviteContextLoad: () =>
    set((state) => {
      state.inviteContextStatus = 'loading';
    }),
  setInviteContextReady: (signUpId, data) =>
    set((state) => {
      state.inviteContext = data;
      state.inviteContextStatus = 'ready';
      state.inviteContextResolvedForSignUpId = signUpId;
    }),
  setInviteContextNotFound: (signUpId) =>
    set((state) => {
      state.inviteContext = undefined;
      state.inviteContextStatus = 'not_found';
      state.inviteContextResolvedForSignUpId = signUpId;
    }),
  setInviteContextFailed: (signUpId) =>
    set((state) => {
      state.inviteContext = undefined;
      state.inviteContextStatus = 'error';
      state.inviteContextResolvedForSignUpId = signUpId;
    }),
  resetInviteContextCache: () =>
    set((state) => {
      state.inviteContext = undefined;
      state.inviteContextStatus = 'idle';
      state.inviteContextResolvedForSignUpId = undefined;
    }),
  setEventIndicatorData: (type: EventIndicatorStateType, message: string) =>
    set((state: OnboardingState) => {
      state.eventIndicatorData = {
        type,
        message,
        index: state.eventIndicatorData?.index
          ? state.eventIndicatorData?.index + 1
          : 1,
      };
      return state;
    }),
  setConfirmationModalData: (confirmationModalData?: ConfirmationModalData) =>
    set({ confirmationModalData }),

  setSignedUpPatient: (signedUpPatient) =>
    set((state) => {
      state.signedUpPatient = signedUpPatient;
    }),
  setSignedUpPatientStatus: (status) =>
    set((state) => {
      state.signedUpPatientStatus = status;
    }),
  retrySignedUpPatientLoad: () =>
    set((state) => {
      state.signedUpPatientStatus = 'loading';
      state.signedUpPatientListenerAttempt += 1;
    }),
  setVerifiedPatientNamesFromAuth: (names) =>
    set((state) => {
      state.verifiedPatientNamesFromAuth = names;
    }),
  resetStore: () => set(() => ({ ...initialState })),
});

type PersistedOnboardingState = Pick<
  OnboardingState,
  'signUpId' | 'verifiedPatientNamesFromAuth' | 'inviteContext'
>;

const PERSIST_STORAGE_KEY = 'webAppOnboarding-default-store-v2';

function partializeOnboardingPersist(state: OnboardingState): PersistedOnboardingState {
  return {
    signUpId: state.signUpId,
    verifiedPatientNamesFromAuth: state.verifiedPatientNamesFromAuth,
    inviteContext: state.inviteContextStatus === 'ready' ? state.inviteContext : undefined,
  };
}

function sanitizeLegacyRehydratedInviteState(persisted: unknown): Partial<OnboardingState> | void {
  if (!persisted || typeof persisted !== 'object') {
    return;
  }
  const record = persisted as Record<string, unknown>;
  const legacyStatus = record.inviteContextStatus;
  if (legacyStatus === 'error' || legacyStatus === 'loading') {
    return {
      inviteContext: undefined,
      inviteContextStatus: 'idle',
      inviteContextResolvedForSignUpId: undefined,
    };
  }
}

function mergePersistedOnboardingState(
  persistedState: unknown,
  currentState: OnboardingState,
): OnboardingState {
  const legacyFix = sanitizeLegacyRehydratedInviteState(persistedState);
  if (legacyFix) {
    return {
      ...currentState,
      ...(persistedState as PersistedOnboardingState),
      ...legacyFix,
    };
  }

  const persisted = (persistedState ?? {}) as PersistedOnboardingState;
  const { signUpId, verifiedPatientNamesFromAuth, inviteContext } = persisted;

  if (inviteContext && signUpId) {
    return {
      ...currentState,
      signUpId,
      verifiedPatientNamesFromAuth,
      inviteContext,
      inviteContextStatus: 'ready',
      inviteContextResolvedForSignUpId: signUpId,
    };
  }

  return {
    ...currentState,
    signUpId,
    verifiedPatientNamesFromAuth,
    inviteContext: undefined,
    inviteContextStatus: 'idle',
    inviteContextResolvedForSignUpId: undefined,
  };
}

export const useOnboardingStore = create<OnboardingState>()(
  immer(
    devtools(
      persist(createOnboardingSlice, {
        name: PERSIST_STORAGE_KEY,
        partialize: partializeOnboardingPersist,
        merge: mergePersistedOnboardingState,
        onRehydrateStorage: () => (state) => {
          if (!state) {
            return;
          }
          if (state.inviteContextStatus === 'error' || state.inviteContextStatus === 'loading') {
            state.resetInviteContextCache();
          }
        },
      }),
      { name: 'webAppOnboardingDefault' },
    ),
  ),
);

/** @param {Object} state Onboarding store snapshot. @return {string | undefined} Active invitation / patient doc id. */
export const getSignUpId = (state: OnboardingState) => state.signUpId;
/** @param {Object} state Onboarding store snapshot. @return {Object | undefined} Cached invite preview from callable. */
export const getInviteContext = (state: OnboardingState) => state.inviteContext;
/** @param {Object} state Onboarding store snapshot. @return {string} Invite callable load status. */
export const getInviteContextStatus = (state: OnboardingState) => state.inviteContextStatus;
/** @param {Object} state Onboarding store snapshot. @return {string | undefined} Sign-up id invite context was resolved for. */
export const getInviteContextResolvedForSignUpId = (state: OnboardingState) =>
  state.inviteContextResolvedForSignUpId;
/** @param {Object} state Onboarding store snapshot. @return {Object | undefined} Full `signedUpPatients` snapshot. */
export const getSignedUpPatient = (state: OnboardingState) =>
  state.signedUpPatient;
/** @param {Object} state Onboarding store snapshot. @return {string} Firestore listener status for the patient doc. */
export const getSignedUpPatientStatus = (state: OnboardingState) =>
  state.signedUpPatientStatus;
/** @param {Object} state Onboarding store snapshot. @return {Object | undefined} Names from successful phone auth callable. */
export const getVerifiedPatientNamesFromAuth = (state: OnboardingState) =>
  state.verifiedPatientNamesFromAuth;
/** @param {Object} state Onboarding store snapshot. @return {string | undefined} Branch deep link for app install. */
export const getAppDeepLink = (state: OnboardingState) =>
  state.signedUpPatient?.deepLink;
/** @param {Object} state Onboarding store snapshot. @return {Object | undefined} Transient toast / banner payload. */
export const getEventIndicatorData = (state: OnboardingState) =>
  state.eventIndicatorData;
/** @param {Object} state Onboarding store snapshot. @return {Object | undefined} Active confirmation modal config. */
export const getConfirmationModalData = (state: OnboardingState) =>
  state.confirmationModalData;
